import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateID } from '../lib/util';
import { HARDWARE_FIRST_MAX_UNITS_PER_CART } from '../lib/hardwareFirst';
import {
  DEFAULT_GIFT_TERM_MONTHS,
  HardwareFirstGiftTermMonths,
  hasAmountGiftCard,
  isAmountGiftCardSku,
} from '../lib/hardwareFirstGiftPlans';
import * as types from '../types';
import { Species } from '../types/gql-op-types';
import {
  recalculateGiftStatus,
  baseCartReducers,
  couponCartReducers,
  giftCardRedemptionCartReducers,
  giftShippingAddressCartReducers,
  referralCodeCartReducers,
} from './baseCart';

/** no catalog here to tell a tracker from an accessory; web rejects an accessory-only gift */
function clearGiftIfOnlyGiftCards(cart: types.StoreCart): types.StoreCart {
  const hasTracker = Object.values(cart.cartItems).some((item) => !('giftCardDelivery' in item));
  if (!cart.isGift || hasTracker) {
    return cart;
  }
  return { ...cart, isGift: false, giftTermMonths: undefined };
}

function withoutAmountGiftCards(cartItems: types.StoreCartItem[]): types.StoreCart['cartItems'] {
  return Object.fromEntries(
    cartItems.filter((item) => !isAmountGiftCardSku(item.lineItem.sku)).map((item) => [item.cartItemId, item]),
  );
}

/** the stash only exists to undo its card, so it goes when the card does */
function dropStashWithoutCard(cart: types.StoreCart): types.StoreCart {
  return hasAmountGiftCard(cart) ? cart : { ...cart, giftCardStash: undefined };
}

interface IRestoreGiftCardStash {
  unitsInCart: number;
  /** false once a trial or ultra joined the cart, which web will not take as a gift */
  offered: boolean;
}

/** swaps the amount cards back for the trackers they replaced, up to the per-cart unit limit */
function restoreGiftCardStash(cart: types.StoreCart, { unitsInCart, offered }: IRestoreGiftCardStash) {
  let room = HARDWARE_FIRST_MAX_UNITS_PER_CART - unitsInCart;
  const restored = (cart.giftCardStash?.cartItems ?? []).flatMap((item) => {
    const quantity = Math.max(Math.min(item.quantity, room), 0);
    room -= quantity;
    return quantity > 0 ? [{ ...item, quantity }] : [];
  });

  return recalculateGiftStatus({
    ...cart,
    cartItems: withoutAmountGiftCards([...Object.values(cart.cartItems), ...restored]),
    ...(offered &&
      restored.length > 0 && {
        isGift: true,
        giftTermMonths: cart.giftCardStash?.giftTermMonths ?? DEFAULT_GIFT_TERM_MONTHS,
      }),
    giftCardStash: undefined,
  });
}

interface IAddSingleLineCartItem {
  sku: string;
  quantity?: number;
  currency: types.FiCurrency;
  ultraAttachment?: boolean;
}

interface IAddBundleCartItem extends IAddSingleLineCartItem {
  subscriptionSku: string;
  addonSkus?: string[];
  quantity?: number;
  isGift?: boolean;
  species?: Species;
}

interface IAddGiftCardCartItem extends IAddSingleLineCartItem {
  giftCardDelivery: types.GiftCardDelivery;
}

interface IAddAmountGiftCard {
  sku: string;
  currency: types.FiCurrency;
  /** the gifted trackers the card replaces; empty when it goes in beside them */
  trackerIds: string[];
}

interface IUpdateCartItemQuantity {
  id: string;
  quantity: number;
}

interface IResetCart {
  resetCoupon?: boolean;
}

export const cartInitialState: types.StoreCart = {
  cartItems: {},
  currency: types.FiCurrency.USD,
  containsAllGiftItems: false,
  containsGiftItems: false,
};

const name = 'storeCart' as const;
export const { actions: cartActions, reducer: cartReducer } = createSlice({
  name,
  initialState: cartInitialState,
  reducers: {
    ...baseCartReducers,
    ...couponCartReducers,
    ...giftCardRedemptionCartReducers,
    ...referralCodeCartReducers,
    ...giftShippingAddressCartReducers,
    addSingleLineCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddSingleLineCartItem>) {
      const singleLineCartItem: types.SingleLineCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        // Use quantity if it was provided or fallback to quantity of '1'
        quantity: payload.quantity && payload.quantity > 0 ? payload.quantity : 1,
        ...(payload.ultraAttachment !== undefined && { ultraAttachment: payload.ultraAttachment }),
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [singleLineCartItem.cartItemId]: singleLineCartItem,
        },
        currency: payload.currency,
      });
    },
    addBundleCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddBundleCartItem>) {
      const bundleCartItem: types.BundleCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        subscriptionLineItem: {
          lineItemId: generateID(),
          sku: payload.subscriptionSku,
        },
        addonLineItems: payload.addonSkus?.map((sku) => ({
          lineItemId: generateID(),
          sku,
        })),
        // Use quantity if it was provided or fallback to quantity of '1'
        quantity: payload.quantity && payload.quantity > 0 ? payload.quantity : 1,
        isGift: payload.isGift || false,
        species: payload.species,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [bundleCartItem.cartItemId]: bundleCartItem,
        },
        currency: payload.currency,
        // hardware-first only sells a bundle as a trial, and web rejects a gift with a trial
        isGift: false,
        giftTermMonths: undefined,
      });
    },
    addGiftCardCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddGiftCardCartItem>) {
      const giftCardCartItem: types.GiftCardCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        giftCardDelivery: payload.giftCardDelivery,

        quantity: 1,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [giftCardCartItem.cartItemId]: giftCardCartItem,
        },
        currency: payload.currency,
      });
    },
    addAmountGiftCard(cart: types.StoreCart, { payload }: PayloadAction<IAddAmountGiftCard>) {
      const stashed = Object.values(cart.cartItems).filter((item) => payload.trackerIds.includes(item.cartItemId));
      const kept = Object.values(cart.cartItems).filter((item) => !payload.trackerIds.includes(item.cartItemId));
      const card: types.GiftCardCartItem = {
        cartItemId: generateID(),
        lineItem: { lineItemId: generateID(), sku: payload.sku },
        // the recipient and delivery are asked for at checkout
        giftCardDelivery: { emailAddress: '' },
        quantity: 1,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: { ...withoutAmountGiftCards(kept), [card.cartItemId]: card },
        currency: payload.currency,
        isGift: false,
        giftTermMonths: undefined,
        giftCardCurrencyNotice: undefined,
        giftCardStash:
          stashed.length > 0
            ? { cartItems: [...(cart.giftCardStash?.cartItems ?? []), ...stashed], giftTermMonths: cart.giftTermMonths }
            : cart.giftCardStash,
      });
    },
    restoreGiftCardStash(cart: types.StoreCart, { payload }: PayloadAction<IRestoreGiftCardStash>) {
      return cart.giftCardStash ? restoreGiftCardStash(cart, payload) : cart;
    },
    /** an amount card is issued in the cart's currency, so a switch swaps it back for its trackers */
    setCurrency(
      cart: types.StoreCart,
      { payload }: PayloadAction<IRestoreGiftCardStash & { currency: types.FiCurrency }>,
    ) {
      const switched = { ...cart, currency: payload.currency };
      if (payload.currency === cart.currency || !hasAmountGiftCard(cart)) {
        return switched;
      }
      return { ...restoreGiftCardStash(switched, payload), giftCardCurrencyNotice: true };
    },
    setGiftCardDelivery(
      cart: types.StoreCart,
      { payload }: PayloadAction<{ id: string; giftCardDelivery: types.GiftCardDelivery }>,
    ) {
      const item = cart.cartItems[payload.id];
      if (item && 'giftCardDelivery' in item) {
        item.giftCardDelivery = payload.giftCardDelivery;
      }
    },
    dismissGiftCardCurrencyNotice(cart: types.StoreCart) {
      cart.giftCardCurrencyNotice = undefined;
    },
    updateCartItemQuantity(cart: types.StoreCart, { payload }: PayloadAction<IUpdateCartItemQuantity>) {
      if (cart.cartItems[payload.id]) {
        cart.cartItems[payload.id].quantity = payload.quantity;
      }
    },
    removeCartItem(cart: types.StoreCart, action: PayloadAction<{ id: string }>) {
      return dropStashWithoutCard(clearGiftIfOnlyGiftCards(baseCartReducers.removeCartItem(cart, action)));
    },
    removeCartItems(cart: types.StoreCart, action: PayloadAction<{ ids: string[] }>) {
      return dropStashWithoutCard(clearGiftIfOnlyGiftCards(baseCartReducers.removeCartItems(cart, action)));
    },

    resetCart(cart: types.StoreCart, { payload }: PayloadAction<IResetCart>) {
      let newState = {
        ...cartInitialState,
        // Currency should be sticky when resetting.
        // Fallback to initial state for users with stale localStorage that lacks this field.
        currency: cart.currency ?? cartInitialState.currency,
      };
      if (!payload.resetCoupon) {
        const couponCode = cart.couponCode;
        newState = { ...newState, couponCode };
      }
      return newState;
    },
    setAwinClick(cart: types.StoreCart, { payload }: PayloadAction<string | undefined>) {
      cart.awinClick = payload;
    },
    setFacebookClick(cart: types.StoreCart, { payload }: PayloadAction<string | undefined>) {
      cart.facebookClick = payload;
    },
    setShowInAppCheckout(cart: types.StoreCart, { payload }: PayloadAction<boolean>) {
      cart.showInAppCheckout = payload;
    },
    setIsGift(cart: types.StoreCart, { payload }: PayloadAction<boolean>) {
      cart.isGift = payload;
      cart.giftTermMonths = payload ? (cart.giftTermMonths ?? DEFAULT_GIFT_TERM_MONTHS) : undefined;
    },
    setGiftTermMonths(cart: types.StoreCart, { payload }: PayloadAction<HardwareFirstGiftTermMonths>) {
      cart.giftTermMonths = payload;
    },
  },
});
